/**
 * Resolve `{ en, fa, ... }` maps to a concrete value for the active locale.
 * Only treats an object as a locale map when every key is a known locale
 * (avoids collapsing `{ icon, to, target }` link objects into a string).
 * Values may be strings or arrays (e.g. multi-image banners).
 */
export function resolveLocalized(
  value: unknown,
  locale: string,
  locales: string[] = ['fa', 'en'],
  fallbackLocale?: string,
): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => resolveLocalized(item, locale, locales, fallbackLocale))
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>
    const keys = Object.keys(record)

    if (keys.length > 0 && isLocaleMap(record, locales)) {
      if (record[locale] !== undefined) return record[locale]
      const fallback = fallbackLocale && record[fallbackLocale] !== undefined
        ? record[fallbackLocale]
        : undefined
      if (fallback !== undefined) return fallback
      for (const code of locales) {
        if (record[code] !== undefined) return record[code]
      }
      return Object.values(record)[0] ?? ''
    }

    return Object.fromEntries(
      Object.entries(record).map(([k, val]) => [
        k,
        resolveLocalized(val, locale, locales, fallbackLocale),
      ]),
    )
  }

  return value
}

function isLocaleMap(
  record: Record<string, unknown>,
  locales: string[],
): boolean {
  const keys = Object.keys(record)
  if (keys.length === 0) return false
  if (!keys.every((k) => locales.includes(k))) return false
  return keys.every((k) => {
    const v = record[k]
    return typeof v === 'string' || Array.isArray(v)
  })
}
